import VerticalDragAndDrop from "@/components-legacy/verticalDragAndDrop";
import { useBoolean } from "@/hooks/use-boolean";
import { getReasonTextFromIdAndLang } from "@/lib/reasons/reason-text-from-id-and-lang";
import {
  ShopDto,
  ShopLocationDto,
  ShopRoutingLocationRuleDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import { Button } from "@babackapp/ui/components/button";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import { TrashIcon } from "@heroicons/react/24/outline";
import { MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";
import { useCarriersContext } from "../../use-carriers-context";
import { AddRoutingLocationRulesDialog } from "../forms/add-routing-location-rules-dialog";
import { EditRoutingLocationRulesDialog } from "../forms/edit-one-carrier-rule-dialog";
import { RemoveOneRuleDialog } from "../forms/remove-one-rule-dialog";
import {
  getRoutingRuleConditions,
  getRoutingRuleLogicalOperator,
  type RoutingCondition,
} from "../lib/routing-location-rules";

export default function RoutingRule(props: { shop: ShopDto }) {
  const t = useTranslation();
  const { editOneCarrierRutingRule } = useCarriersContext();
  const { data: shopifyLocations } = useShopifyShopLocations(props.shop.id);
  const openDeleteCarrer = useBoolean(false);
  const openEditCarrer = useBoolean(false);

  const [selectedRule, setSelectedRule] =
    useState<ShopRoutingLocationRuleDto | null>(null);

  const onClickDeleteOneRule = (rule: ShopRoutingLocationRuleDto) => {
    setSelectedRule(rule);
    openDeleteCarrer.setTrue();
  };

  const onClickEditOneRule = (rule: ShopRoutingLocationRuleDto) => {
    setSelectedRule(rule);
    openEditCarrer.setTrue();
  };

  const describeConditionValue = (condition: RoutingCondition) => {
    if (condition.key === "reasonId") {
      return (
        getReasonTextFromIdAndLang(
          condition.value,
          props.shop.preferences.languages[0],
          props.shop,
        ) || condition.value
      );
    }

    if (condition.key === "fulfillmentOriginLocationId") {
      return (
        shopifyLocations?.find((location) => location.id === condition.value)
          ?.name ?? condition.value
      );
    }

    return condition.value;
  };

  const describeRule = (rule: ShopRoutingLocationRuleDto) => {
    const conditions = getRoutingRuleConditions(rule);

    // Règle laissée inerte par la migration : on l'affiche explicitement plutôt
    // que de rendre une phrase vide, pour que le marchand puisse la corriger.
    if (!conditions.length) {
      return t("settings.shipping.rules.noCondition");
    }

    const operatorLabel = t(
      `misc.${getRoutingRuleLogicalOperator(rule).toLowerCase() as "and" | "or"}`,
    );

    return [
      t("settings.shipping.rules.conditionIf"),
      conditions
        .map((condition) =>
          t(`settings.shipping.rules.condition.${condition.key}`, {
            value: describeConditionValue(condition),
          }),
        )
        .join(` ${operatorLabel} `),
    ].join(" ");
  };

  return (
    <div className="pt-2">
      {selectedRule && openDeleteCarrer.value && (
        <RemoveOneRuleDialog
          isOpen={openDeleteCarrer.value}
          setIsOpen={openDeleteCarrer.setValue}
          selectedRule={selectedRule}
          onCloseForm={openDeleteCarrer.setFalse}
        />
      )}

      {selectedRule && openEditCarrer.value && (
        <EditRoutingLocationRulesDialog
          isOpen={openEditCarrer.value}
          setIsOpen={openEditCarrer.setValue}
          selectedRule={selectedRule}
          onCloseForm={openEditCarrer.setFalse}
        />
      )}
      <div className="">{t("settings.shipping.rules.title")}</div>
      <VerticalDragAndDrop<
        ShopRoutingLocationRuleDto & {
          id: string;
        }
      >
        items={
          props.shop.routingLocationRules as (ShopRoutingLocationRuleDto & {
            id: string;
          })[]
        }
        onChange={editOneCarrierRutingRule}
        itemRenderer={(rule) => {
          const location = props.shop.locations.find(
            (loc: ShopLocationDto) => loc.id === rule.positiveResult,
          );
          return (
            <div className="border-gray-200 border rounded-md my-2 py-4 px-6 bg-white text-sm flex">
              <div className="flex-1">
                <div className="text-gray-900">{describeRule(rule)}</div>
                <div className="text-gray-500 font-light">
                  {t("settings.shipping.rules.sendItToLocation", {
                    location: location?.address?.companyName,
                  })}
                </div>
              </div>
              <div className="">
                <SimpleMenu
                  iconTitle={<MoreHorizontal className="h-4 w-4" />}
                  position="left"
                  type={"neutral"}
                  menu={[
                    {
                      onClick: () => {
                        onClickEditOneRule(rule);
                      },
                      title: t("settings.integrations.webhooks.menu.edit"),
                      icon: <Pencil className="h-4 w-4 mr-1" />,
                    },
                    {
                      onClick: () => {
                        onClickDeleteOneRule(rule);
                      },
                      renderTitle: () => (
                        <p className="text-red-500">{t("misc.delete")}</p>
                      ),
                      icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
                    },
                  ]}
                />
              </div>
            </div>
          );
        }}
      />

      <div className="border-gray-200 border rounded-md my-2 py-4 px-6 bg-white text-sm ">
        <div className="text-gray-900">
          {t("settings.shipping.rules.default")}
        </div>
        <div className="text-gray-500 font-light">
          {t("settings.shipping.rules.sendItToLocation", {
            location: props.shop.locations?.find(
              (location: ShopLocationDto) => location.isDefault,
            )?.address?.companyName,
          })}
        </div>
      </div>
      <AddRoutingLocationRulesDialog>
        <Button variant="secondary">
          {t("settings.shipping.rules.addRule")}
        </Button>
      </AddRoutingLocationRulesDialog>
    </div>
  );
}
